import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CheckCircle2, AlertTriangle, XCircle, ExternalLink } from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import WebServiceCard from "../components/WebServiceCard";
import AddWebServiceDialog from "../components/AddWebServiceDialog";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

// Renders a compact live status of the KRAI CONNECT → KRAI API → Playwright
// chain at the top of the page. Uses the same /browser/health endpoint as
// the Navegador module so both views agree; that endpoint is cached 10s in
// the backend so refreshing this page is cheap.
function HealthBanner({ health, loading, onRetry }) {
  if (loading && !health) {
    return (
      <div
        data-testid={TID.webServices.healthBanner}
        className="mb-6 rounded-md border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-500"
      >
        Comprobando el estado de KRAI API…
      </div>
    );
  }
  if (!health) return null;

  const isOk = health.ok === true;
  const isUnconfigured = health.code === "krai_api_not_configured";
  const style = isOk
    ? { wrap: "border-emerald-900/40 bg-emerald-950/20", text: "text-emerald-300", head: "text-emerald-200", Icon: CheckCircle2 }
    : isUnconfigured
      ? { wrap: "border-zinc-800 bg-zinc-950", text: "text-zinc-400", head: "text-zinc-300", Icon: AlertTriangle }
      : { wrap: "border-red-900/40 bg-red-950/20", text: "text-red-300", head: "text-red-200", Icon: XCircle };
  const Icon = style.Icon;

  return (
    <div
      data-testid={TID.webServices.healthBanner}
      className={`mb-6 rounded-md border p-4 flex items-start gap-3 ${style.wrap}`}
    >
      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${style.text}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-semibold mb-1 ${style.head}`}>
          {isOk
            ? "KRAI API operativa"
            : isUnconfigured
              ? "KRAI API sin configurar"
              : "KRAI API inaccesible"}
        </div>
        <div className={`text-[11px] leading-relaxed ${style.text}`}>
          {health.message}
          {typeof health.latency_ms === "number" && (
            <span className="font-mono text-zinc-500"> · {health.latency_ms} ms</span>
          )}
          {health.playwright_version && (
            <span className="font-mono text-zinc-500"> · pw {health.playwright_version}</span>
          )}
        </div>
        {isUnconfigured && (
          <Link
            to="/krai-api"
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-300 hover:text-white underline underline-offset-2"
          >
            Configurar KRAI API <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
      <button
        onClick={onRetry}
        disabled={loading}
        className="text-[11px] text-zinc-400 hover:text-white shrink-0 disabled:opacity-40"
      >
        {loading ? "…" : "Reintentar"}
      </button>
    </div>
  );
}

export default function WebServices() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => {
    load();
    loadHealth();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/web-services");
      setList(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadHealth() {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/browser/health");
      setHealth(data);
    } catch (e) {
      setHealth({ ok: false, code: "krai_api_error", message: apiErrorMessage(e) });
    } finally {
      setHealthLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }
  function openEdit(service) {
    setEditing(service);
    setDialogOpen(true);
  }
  function onCreated(s) {
    setList((prev) => [...prev, s]);
  }
  function onUpdated(s) {
    setList((prev) => prev.map((x) => (x.id === s.id ? s : x)));
  }
  function onChange(s) {
    setList((prev) => prev.map((x) => (x.id === s.id ? s : x)));
  }
  function onDeleted(id) {
    setList((prev) => prev.filter((x) => x.id !== id));
  }

  return (
    <div data-testid={TID.webServices.root}>
      <PageHeader
        eyebrow="Catálogo"
        title="Servicios web"
        description="Registra los servicios web que gestionará KRAI CONNECT. Puedes vincular cada servicio con un perfil del módulo Navegador para verificarlo end-to-end mediante KRAI API."
        actions={
          <Button
            data-testid={TID.webServices.addButton}
            onClick={openCreate}
            className="bg-white text-black hover:bg-zinc-200 h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Añadir servicio
          </Button>
        }
      />

      <HealthBanner health={health} loading={healthLoading} onRetry={loadHealth} />

      {loading ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Cargando servicios…
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Aún no hay servicios web. Crea el primero con el botón "Añadir servicio".
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((s) => (
            <WebServiceCard
              key={s.id}
              service={s}
              onChange={onChange}
              onDeleted={onDeleted}
              onEdit={openEdit}
            />
          ))}
        </div>
      )}

      <AddWebServiceDialog
        key={editing?.id || "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={onCreated}
        onUpdated={onUpdated}
        editing={editing}
      />
    </div>
  );
}
